
// export default TutorLogin;
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "../components/ui/use-toast";
import { useResourcePersonAuth } from "../store/resourcePerson/resourcePersonStore";
import authSession from "../lib/authSession";
import { goAfterLogin } from "../lib/roles";
import Logo from "../assets/fillopsLogo.png";

const tutorLoginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const TutorLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login, forgotPassword } = useResourcePersonAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Password recovery. The API has POST /auth/forgot-password, so the link is
  // real: it posts the address and tells the user to check their inbox.
  const [recovering, setRecovering] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState(null);

  const requestReset = async (event) => {
    event.preventDefault();
    const email = recoveryEmail.trim();
    if (!email) return;
    setRecoveryBusy(true);
    setRecoveryMessage(null);
    const result = await forgotPassword(email);
    setRecoveryBusy(false);
    if (!result.success) {
      setRecoveryMessage({ ok: false, text: result.error || "Could not start password recovery." });
      return;
    }
    setRecoveryMessage({
      ok: true,
      text: "If that address is registered, a reset link is on its way. Check your inbox.",
    });
  };

  const closeRecovery = () => {
    setRecovering(false);
    setRecoveryMessage(null);
    setRecoveryEmail("");
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(tutorLoginSchema),
  });

  useEffect(() => {
    console.log("🔧 TutorLogin Component Loaded");
    console.log("📍 useNavigate hook initialized:", typeof navigate);
  }, [navigate]);

  const onSubmit = async (formData) => {
    setError("");
    setLoading(true);
    
    try {
      const result = await login(formData.email, formData.password);
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.info("[auth] RP login result", result);
      }
      if (!result.success) {
        setError(result.error || "Login failed");
        return;
      }

      const user = result.user || {};
      // Shared identity + redirect helpers — the same source of truth the route
      // guards read, so the portal can never disagree about who is signed in.
      authSession.persistIdentity("resource-person", { email: formData.email, user });

      // Show toast
      toast({
        title: "Welcome back! 👨‍🏫",
        description: "Successfully logged in as ResourcePerson.",
      });

      // Navigate by role (honours ?next= only inside the resource-person area)
      goAfterLogin(navigate, { role: "resource-person" });
      
    } catch (err) {
      console.error("❌ Network Error:", err);
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-white dark:bg-background px-3 py-6 transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative w-full max-w-sm bg-white dark:bg-background border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg px-6 py-12 overflow-hidden transition-colors duration-300"
      >
        {/* Decorative Star */}
        <div className="absolute -top-11 -right-9 w-32 h-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-purple-200 via-blue-200 to-transparent blur-2xl opacity-100 rounded-full" />
            {[...Array(6)].map((_, i) => (
              <motion.span
                key={i}
                className="absolute bg-white rounded-full opacity-80"
                style={{
                  top: `${Math.random() * 80}%`,
                  left: `${Math.random() * 80}%`,
                  width: `${Math.random() * 4 + 2}px`,
                  height: `${Math.random() * 4 + 2}px`,
                  filter: "blur(1px)",
                }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: Math.random() * 3 + 2, repeat: Infinity, ease: "easeInOut" }}
              />
            ))}
            <div
              className="relative w-32 h-32"
              style={{ clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)" }}
            >
              <img
                src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1920&h=1080&fit=crop"
                alt="Decorative"
                className="w-full h-full object-cover shadow-lg border border-white"
              />
            </div>
          </motion.div>
        </div>

        {/* Logo & Badge */}
        <div className="flex justify-center mb-3 relative">
          <div className="relative">
            <img src={Logo} alt="App Logo" className="h-14 w-14 rounded-full shadow-md object-cover" />
            <div className="absolute -bottom-1 -right-1 bg-purple-600 rounded-full p-1.5 shadow-lg">
              <GraduationCap className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-3">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Welcome Back 
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Sign in to your resource person dashboard</p>
        </div>

        {/* Form */}
        {recovering ? (
          /* Recovery — POST /api/resource-person/auth/forgot-password */
          <form onSubmit={requestReset} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter the email you registered with and we&apos;ll send a password reset link.
            </p>

            <div className="relative">
              <Mail className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
              <input
                type="email"
                required
                autoComplete="email"
                placeholder="Resource Person Email Address"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-purple-400 transition border-gray-300 dark:border-gray-700"
              />
            </div>

            {recoveryMessage ? (
              <p
                className={`text-sm text-center ${
                  recoveryMessage.ok ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                }`}
              >
                {recoveryMessage.text}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={recoveryBusy || !recoveryEmail.trim()}
              className="w-full py-2.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60 transition font-medium"
            >
              {recoveryBusy ? "Sending…" : "Send reset link"}
            </button>

            <button
              type="button"
              onClick={closeRecovery}
              className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-purple-600 transition"
            >
              Back to sign in
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
            <input
              type="email"
              placeholder="Resource Person Email Address"
              {...register("email")}
              className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-purple-400 transition ${
                errors.email ? "border-red-400" : "border-gray-300 dark:border-gray-700"
              }`}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
          </div>

          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              {...register("password")}
              className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-purple-400 transition ${
                errors.password ? "border-red-400" : "border-gray-300 dark:border-gray-700"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
            <button
              type="button"
              onClick={() => setRecovering(true)}
              className="block w-full text-right text-xs text-gray-500 dark:text-gray-400 hover:text-purple-600 transition"
            >
              Forgot your password?
            </button>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
            >
              <p className="text-red-600 dark:text-red-400 text-sm text-center">{error}</p>
            </motion.div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 ${
              loading ? "bg-purple-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700"
            } text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200`}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Logging in...</>
            ) : (
              <><GraduationCap className="w-4 h-4" /> Log In as Resource Person</>
            )}
          </button>

          {/* Links */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-600 dark:text-gray-400">
            <p className="hover:text-purple-600 dark:hover:text-purple-400 cursor-pointer">Forgot Password?</p>
            <p
              onClick={() => navigate("/resource-person")}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 hover:underline cursor-pointer"
            >
              Register as Resource Person
            </p>
          </div>

        </form>
        )}

        <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 mt-6">
          © {new Date().getFullYear()} CBT Project. All rights reserved.
        </p>
      </motion.div>
    </section>
  );
};

export default TutorLogin;